import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceAlertsPanel = ({ kycStatus = 'pending' }) => {
  const navigate = useNavigate();

  const getKycAlerts = () => {
    switch (kycStatus) {
      case 'pending':
        return [
          {
            id: 'kyc-pending',
            type: 'warning',
            title: 'KYC Verification Pending',
            message: 'Complete your identity verification to unlock full account features and higher transfer limits.',
            action: 'Complete KYC',
            actionPath: '/document-upload',
            icon: 'AlertTriangle'
          }
        ];
      case 'under_review':
        return [
          {
            id: 'kyc-review',
            type: 'info',
            title: 'Documents Under Review',
            message: 'Your KYC documents are being reviewed. This typically takes 1-2 business days.',
            action: 'View Status',
            actionPath: '/profile-management',
            icon: 'Clock'
          }
        ];
      case 'rejected':
        return [
          {
            id: 'kyc-rejected',
            type: 'error',
            title: 'KYC Verification Failed',
            message: 'Your documents need to be resubmitted. Please check the requirements and try again.',
            action: 'Resubmit Documents',
            actionPath: '/document-upload',
            icon: 'XCircle'
          }
        ];
      case 'verified':
        return [
          {
            id: 'limit-increase',
            type: 'success',
            title: 'Increase Transfer Limits',
            message: 'You\'re eligible for higher transfer limits. Upgrade your account for enhanced features.',
            action: 'Upgrade Account',
            actionPath: '/profile-management',
            icon: 'TrendingUp'
          }
        ];
      default:
        return [];
    }
  };

  const generalAlerts = [
    {
      id: 'security-tip',
      type: 'info',
      title: 'Security Reminder',
      message: 'Always verify recipient details before sending money. We will never ask for your password via email.',
      action: 'Security Tips',
      actionPath: '/profile-management',
      icon: 'Shield'
    }
  ];

  const allAlerts = [...getKycAlerts(), ...generalAlerts];

  const getAlertStyles = (type) => {
    switch (type) {
      case 'error':
        return 'border-error/20 bg-error/5';
      case 'warning':
        return 'border-warning/20 bg-warning/5';
      case 'success':
        return 'border-success/20 bg-success/5';
      case 'info':
      default:
        return 'border-accent/20 bg-accent/5';
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'error':
        return 'var(--color-error)';
      case 'warning':
        return 'var(--color-warning)';
      case 'success':
        return 'var(--color-success)';
      case 'info':
      default:
        return 'var(--color-accent)';
    }
  };

  if (allAlerts?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-foreground mb-4">Important Notifications</h2>
      <div className="space-y-4">
        {allAlerts?.map((alert) => (
          <div key={alert?.id} className={`border rounded-lg p-4 ${getAlertStyles(alert?.type)}`}>
            <div className="flex items-start space-x-3">
              <Icon 
                name={alert?.icon} 
                size={20} 
                color={getIconColor(alert?.type)}
                className="mt-0.5 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-foreground mb-1">
                  {alert?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {alert?.message}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(alert?.actionPath)}
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={14}
                >
                  {alert?.action}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComplianceAlertsPanel;